import RenderEntity, { RENDER_ENTITY_FORMAT3 } from 'ui-component/renders/RenderEntity';
import { lazy } from 'react';

// material-ui
import { Box, Typography, Tooltip } from '@mui/material';

// third-party
import { format, parseJSON } from 'date-fns';
import { FormattedMessage } from 'react-intl';

// project imports
import { appState, appGridActions } from 'store/behaviors/loader';
import capabilities from './capabilities';
import RenderEntityList, { RENDER_ENTITYLIST_FORMAT2 } from 'ui-component/renders/RenderEntityList';
import Avatar from 'ui-component/extended/Avatar';

// assets
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockSharpIcon from '@mui/icons-material/LockSharp';
import DisabledByDefaultRoundedIcon from '@mui/icons-material/DisabledByDefaultRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PersonIcon from '@mui/icons-material/Person';
import HowToRegTwoToneIcon from '@mui/icons-material/HowToRegTwoTone';
import PersonOffTwoToneIcon from '@mui/icons-material/PersonOffTwoTone';
import KeyOffTwoToneIcon from '@mui/icons-material/KeyOffTwoTone';
import WifiOffTwoToneIcon from '@mui/icons-material/WifiOffTwoTone';
import { renderUser } from 'ui-component/fields/EntitySelect/admin/UserSelect';

// lazy loaded components
const EmployeeSelect = lazy(() => import('ui-component/fields/EntitySelect/common/EmployeeSelect'));
const UserSelect = lazy(() => import('ui-component/fields/EntitySelect/admin/UserSelect'));

const initialState = {
    ...appState(capabilities),
    colDef: [
        {
            field: 'actions',
            type: 'actions',
            description: '',
            align: 'center',
            hideable: false,
            sortable: false,
            resizable: false,
            disableReorder: true,
            label: 'ui-comp-table-actions',
            getActions: (params, permissionData, onClickActions) => {
                const gridActions = appGridActions(capabilities, params?.row, permissionData, onClickActions);

                permissionData?.forEach((perm) => {
                    switch (perm) {
                        case 'canApproveUser':
                            if (params?.row?.deletedAt || params?.row?.enabled) {
                                return;
                            }

                            gridActions.push({
                                key: `actionapprove-row${params?.row?.id}`,
                                icon: <HowToRegTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-approveuser',
                                position: 7,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionApproveUser' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionApproveUser === 'function'
                                    ) {
                                        onClickActions.onClickRowActionApproveUser(params?.row);
                                    }
                                }
                            });
                            break;
                        case 'canRejectUser':
                            if (params?.row?.deletedAt || params?.row?.enabled) {
                                return;
                            }

                            gridActions.push({
                                key: `actionreject-row${params?.row?.id}`,
                                icon: <PersonOffTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-rejectuser',
                                position: 8,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionRejectUser' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionRejectUser === 'function'
                                    ) {
                                        onClickActions.onClickRowActionRejectUser(params?.row);
                                    }
                                }
                            });
                            break;
                        case 'canResetPassword':
                            if (params?.row?.deletedAt || !params?.row?.enabled) {
                                return;
                            }

                            gridActions.push({
                                key: `actionresetpassword-row${params?.row?.id}`,
                                icon: <KeyOffTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-resetpassword',
                                position: 9,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionResetPassword' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionResetPassword === 'function'
                                    ) {
                                        onClickActions.onClickRowActionResetPassword(params?.row);
                                    }
                                }
                            });
                            break;
                        case 'canDeleteConnections':
                            if (params?.row?.deletedAt || !params?.row?.activeConnections?.length) {
                                return;
                            }

                            gridActions.push({
                                key: `actiondeleteconnections-row${params?.row?.id}`,
                                icon: <WifiOffTwoToneIcon fontSize="small" />,
                                label: 'ui-comp-button-deleteconnections',
                                position: 9,
                                showInMenu: true,
                                onClick: () => {
                                    if (
                                        'onClickRowActionDeleteConnections' in (onClickActions || {}) &&
                                        typeof onClickActions.onClickRowActionDeleteConnections === 'function'
                                    ) {
                                        onClickActions.onClickRowActionDeleteConnections(params?.row);
                                    }
                                }
                            });
                            break;
                        default: // do nothing
                    }
                });

                return gridActions;
            }
        },
        {
            field: 'id',
            type: 'number',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            label: 'traits-appentity-id'
        },
        {
            field: 'avatar',
            type: 'string',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            renderCell: (params) =>
                params?.value ? (
                    <Avatar size="xs" alt="" avatar={params.value} />
                ) : (
                    <Avatar size="xs" alt="">
                        <PersonIcon fontSize="small" />
                    </Avatar>
                ),

            label: 'entity-admin-user-avatar'
        },
        {
            field: 'username',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-admin-user-username'
        },
        {
            field: 'email',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-admin-user-email'
        },
        {
            field: 'name',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'traits-appentityisdescribable-name'
        },
        {
            field: 'lastName',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-admin-user-lastname'
        },
        {
            field: 'description',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            flex: 1,
            label: 'traits-appentityisdescribable-description'
        },
        {
            field: 'employee',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntity
                    entityName="employee"
                    renderPattern="<name> <lastName>"
                    renderTooltipPattern="[<code>] <name> <lastName>"
                    renderFormat={RENDER_ENTITY_FORMAT3}
                    {...params}
                />
            ),
            label: 'entity-common-employee'
        },
        {
            field: 'groupsNames',
            type: 'singleSelect',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-admin-group-plural'
        },
        {
            field: 'lastLogin',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-admin-user-lastlogin'
        },
        {
            field: 'lastLogout',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-admin-user-lastlogout'
        },
        {
            field: 'passwordExpirationTime',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-admin-user-passwordexpirationtime'
        },
        {
            field: 'passwordNeverExpires',
            type: 'boolean',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'entity-admin-user-passwordneverexpires'
        },
        {
            field: 'lastPasswordChanged',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'entity-admin-user-lastpasswordchanged'
        },
        {
            field: 'enabled',
            type: 'boolean',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <Box>
                    {params?.value ? (
                        <CheckCircleRoundedIcon fontSize="small" color="info" />
                    ) : (
                        <DisabledByDefaultRoundedIcon fontSize="small" color="error" />
                    )}
                </Box>
            ),
            label: 'entity-admin-user-enabled'
        },
        {
            field: 'rejectionReason',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            label: 'entity-admin-user-rejectionreason'
        },
        {
            field: 'activeConnections',
            type: 'singleSelect',
            description: '',
            align: 'center',
            hideable: true,
            sortable: false,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <RenderEntityList entityName="activeConnection" renderPattern="<ip>" renderFormat={RENDER_ENTITYLIST_FORMAT2} {...params} />
            ),
            label: 'entity-admin-activeconnection-plural'
        },
        {
            field: 'locked',
            type: 'boolean',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => (
                <Box>
                    {params?.value ? (
                        <LockSharpIcon fontSize="small" color="error" />
                    ) : (
                        <LockOpenOutlinedIcon fontSize="small" color="info" />
                    )}
                </Box>
            ),
            label: 'traits-appentityislockable-locked'
        },
        {
            field: 'lockedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityislockable-lockedat'
        },
        {
            field: 'lockedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityislockable-lockedby'
        },
        {
            field: 'lockType',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={<FormattedMessage id={`ui-comp-form-locktype-${params.value}`} />}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FormattedMessage id={`ui-comp-form-locktype-${params.value}`} />
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityislockable-locktype'
        },
        {
            field: 'lockTimeout',
            type: 'number',
            description: '',
            align: 'center',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            label: 'traits-appentityislockable-locktimeout'
        },
        {
            field: 'createdAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-createdat'
        },
        {
            field: 'createdBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-createdby'
        },
        {
            field: 'updatedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentity-updatedat'
        },
        {
            field: 'updatedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentity-updatedby'
        },
        {
            field: 'deletedAt',
            type: 'dateTime',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            valueGetter: (params) => params?.value && parseJSON(params.value),
            renderCell: (params) =>
                params?.value && (
                    <Tooltip title={format(new Date(params.value), 'd/MM/yyyy HH:mm')}>
                        <Typography
                            variant="inherit"
                            color="inherit"
                            sx={{
                                overflow: 'hidden',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {format(new Date(params.value), 'd/MM/yyyy HH:mm')}
                        </Typography>
                    </Tooltip>
                ),
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            field: 'deletedBy',
            type: 'string',
            description: '',
            align: 'left',
            hideable: true,
            sortable: true,
            resizable: true,
            exportable: true,
            renderCell: (params) => renderUser(params?.value, 'fullName'),
            label: 'traits-appentityissafedeletable-deletedby'
        }
    ],
    dataGridState: {
        columns: {
            columnVisibilityModel: {
                id: false,
                avatar: true,
                username: true,
                email: true,
                name: true,
                lastName: true,
                description: false,
                employee: true,
                groupsNames: true,
                lastLogin: true,
                lastLogout: true,
                passwordExpirationTime: false,
                passwordNeverExpires: false,
                lastPasswordChanged: false,
                rejectionReason: false,
                enabled: true,
                activeConnections: true,
                locked: true,
                lockedAt: true,
                lockedBy: false,
                lockType: false,
                lockTimeout: true,
                createdAt: true,
                createdBy: true,
                updatedAt: false,
                updatedBy: false,
                deletedAt: false,
                deletedBy: false
            },
            orderedFields: [
                'actions',
                'id',
                'avatar',
                'username',
                'email',
                'name',
                'lastName',
                'description',
                'employee',
                'groupsNames',
                'lastLogin',
                'lastLogout',
                'passwordExpirationTime',
                'passwordNeverExpires',
                'lastPasswordChanged',
                'rejectionReason',
                'enabled',
                'activeConnections',
                'locked',
                'lockedAt',
                'lockedBy',
                'lockType',
                'lockTimeout',
                'createdAt',
                'createdBy',
                'updatedAt',
                'updatedBy',
                'deletedAt',
                'deletedBy'
            ],
            dimensions: {
                actions: { width: 60 },
                id: { width: 100 },
                avatar: { width: 80 },
                username: { width: 100 },
                email: { width: 120 },
                name: { width: 120 },
                lastName: { width: 120 },
                description: { width: 100 },
                employee: { width: 120 },
                groupsNames: { width: 120 },
                lastLogin: { width: 100 },
                lastLogout: { width: 100 },
                passwordExpirationTime: { width: 100 },
                passwordNeverExpires: { width: 100 },
                lastPasswordChanged: { width: 100 },
                enabled: { width: 100 },
                activeConnections: { width: 100 },
                locked: { width: 100 },
                lockedAt: { width: 100 },
                lockedBy: { width: 100 },
                lockType: { width: 100 },
                lockTimeout: { width: 100 },
                createdAt: { width: 100 },
                createdBy: { width: 100 },
                updatedAt: { width: 100 },
                updatedBy: { width: 100 },
                deletedAt: { width: 100 },
                deletedBy: { width: 100 }
            }
        },
        sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }]
        },
        toolbarFilters: { includeSafeDeleted: false }
    },
    filterDef: [
        {
            filterType: 'string',
            field: { fieldName: 'username', fieldType: 'simple' },
            label: 'entity-admin-user-username'
        },
        {
            filterType: 'string',
            field: { fieldName: 'email', fieldType: 'simple' },
            label: 'entity-admin-user-email'
        },
        {
            filterType: 'string',
            field: { fieldName: 'name', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-name'
        },
        {
            filterType: 'string',
            field: { fieldName: 'lastName', fieldType: 'simple' },
            label: 'entity-admin-user-lastname'
        },
        {
            filterType: 'string',
            field: { fieldName: 'description', fieldType: 'simple' },
            label: 'traits-appentityisdescribable-description'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'employeeId', fieldType: 'simple' },
            label: 'entity-common-employee',
            renderField: (params) => <EmployeeSelect key="filterUser-employee" multiple {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'lastLogin', fieldType: 'simple' },
            label: 'entity-admin-user-lastlogin'
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'lastLogout', fieldType: 'simple' },
            label: 'entity-admin-user-lastlogout'
        },
        {
            filterType: 'number',
            field: { fieldName: 'passwordExpirationTime', fieldType: 'simple' },
            label: 'entity-admin-user-passwordexpirationtime'
        },
        {
            filterType: 'boolean',
            field: { fieldName: 'passwordNeverExpires', fieldType: 'simple' },
            label: 'entity-admin-user-passwordneverexpires'
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'lastPasswordChanged', fieldType: 'simple' },
            label: 'entity-admin-user-lastpasswordchanged'
        },
        {
            filterType: 'boolean',
            field: { fieldName: 'enabled', fieldType: 'simple' },
            label: 'entity-admin-user-enabled'
        },
        {
            filterType: 'string',
            field: { fieldName: 'rejectionReason', fieldType: 'simple' },
            label: 'entity-admin-user-rejectionreason'
        },
        {
            filterType: 'boolean',
            field: { fieldName: 'locked', fieldType: 'simple' },
            label: 'traits-appentityislockable-locked'
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'lockedAt', fieldType: 'simple' },
            label: 'traits-appentityislockable-lockedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'lockedBy', fieldType: 'simple', outputFieldName: 'lockedById' },
            label: 'traits-appentity-lockedby',
            renderField: (params) => <UserSelect key="filterUser-lockedBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'string',
            field: { fieldName: 'lockReason', fieldType: 'simple' },
            label: 'traits-appentityislockable-locktype'
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'createdAt', fieldType: 'simple' },
            label: 'traits-appentity-createdat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'createdBy', fieldType: 'simple', outputFieldName: 'createdById' },
            label: 'traits-appentity-createdby',
            renderField: (params) => <UserSelect key="filterUser-createdBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'updatedAt', fieldType: 'simple' },
            label: 'traits-appentity-updatedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'updatedBy', fieldType: 'simple', outputFieldName: 'updatedById' },
            label: 'traits-appentity-updatedby',
            renderField: (params) => <UserSelect key="filterUser-updatedBy" multiple displayMode="fullName" {...params} />
        },
        {
            filterType: 'dateTime',
            field: { fieldName: 'deletedAt', fieldType: 'simple' },
            label: 'traits-appentityissafedeletable-deletedat'
        },
        {
            filterType: 'reference',
            field: { fieldName: 'deletedBy', fieldType: 'simple', outputFieldName: 'deletedById' },
            label: 'traits-appentityissafedeletable-deletedby',
            renderField: (params) => <UserSelect key="filterUser-deletedBy" multiple displayMode="fullName" {...params} />
        }
    ],
    quickSearchFilterDef: {
        placeholder: 'ui-comp-quicksearchplaceholder-user',
        fields: [
            { fieldName: 'username', fieldType: 'simple' },
            { fieldName: 'email', fieldType: 'simple' },
            { fieldName: 'name', fieldType: 'simple' },
            { fieldName: 'lastName', fieldType: 'simple' },
            { fieldName: 'description', fieldType: 'simple' },
            { fieldName: 'groupsNames', fieldType: 'simple' },
            { fieldName: 'employeeCode', fieldType: 'simple' },
            { fieldName: 'employeeName', fieldType: 'simple' },
            { fieldName: 'employeeLastName', fieldType: 'simple' }
        ]
    },
    sortingDef: [
        { fieldName: 'id', fieldType: 'simple' },
        { fieldName: 'username', fieldType: 'simple' },
        { fieldName: 'email', fieldType: 'simple' },
        { fieldName: 'name', fieldType: 'simple' },
        { fieldName: 'lastName', fieldType: 'simple' },
        { fieldName: 'employeeId', fieldType: 'simple' },
        { fieldName: 'lastLogin', fieldType: 'simple' },
        { fieldName: 'lastLogout', fieldType: 'simple' },
        { fieldName: 'passwordExpirationTime', fieldType: 'simple' },
        { fieldName: 'passwordNeverExpires', fieldType: 'simple' },
        { fieldName: 'lastPasswordChanged', fieldType: 'simple' },
        { fieldName: 'enabled', fieldType: 'simple' },
        { fieldName: 'locked', fieldType: 'simple' },
        { fieldName: 'lockedAt', fieldType: 'simple' },
        { fieldName: 'lockedBy', fieldType: 'simple' },
        { fieldName: 'createdAt', fieldType: 'simple' },
        { fieldName: 'createdBy', fieldType: 'simple' },
        { fieldName: 'updatedAt', fieldType: 'simple' },
        { fieldName: 'updatedBy', fieldType: 'simple' },
        { fieldName: 'deletedAt', fieldType: 'simple' },
        { fieldName: 'deletedBy', fieldType: 'simple' }
    ]
};

export default initialState;
